import { AnimatePresence, motion } from 'motion/react';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { Subscription } from '../../lib/types';

interface SubscriptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddSubscription: (sub: Omit<Subscription, 'id' | 'uid'>) => Promise<void>;
  categories: string[];
}

export function SubscriptionsModal({
  isOpen,
  onClose,
  onAddSubscription,
  categories,
}: SubscriptionsModalProps) {
  const [newSub, setNewSub] = useState({ note: '', category: '', amount: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSub.note || !newSub.category) return;

    await onAddSubscription({
      note: newSub.note,
      category: newSub.category,
      amount: parseFloat(newSub.amount) || 0,
    });
    setNewSub({ note: '', category: '', amount: '' });
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-[32px] border border-zinc-100 bg-white shadow-2xl transition-colors dark:border-zinc-900 dark:bg-black"
          >
            <div className="flex items-center justify-between border-b border-zinc-100 p-6 dark:border-zinc-900">
              <div>
                <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                  Add Subscription
                </h2>
                <p className="text-xs font-medium text-zinc-500">
                  Add a recurring expense
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-full p-2 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                <X className="h-5 w-5 text-zinc-400" />
              </button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                    Name
                  </label>
                  <input
                    required
                    placeholder="e.g. Netflix"
                    className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-white"
                    value={newSub.note}
                    onChange={(e) =>
                      setNewSub({ ...newSub, note: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                    Category
                  </label>
                  <select
                    required
                    className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-white"
                    value={newSub.category}
                    onChange={(e) =>
                      setNewSub({ ...newSub, category: e.target.value })
                    }
                  >
                    <option value="" disabled>
                      Select
                    </option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                    Amount
                  </label>
                  <input
                    type="number"
                    placeholder="0.00"
                    step="0.01"
                    className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-white"
                    value={newSub.amount}
                    onChange={(e) =>
                      setNewSub({ ...newSub, amount: e.target.value })
                    }
                  />
                </div>
                <button
                  type="submit"
                  className="flex h-[48px] w-full items-center justify-center gap-2 rounded-2xl bg-zinc-900 font-bold text-white shadow-lg transition-opacity hover:opacity-90 active:scale-[0.98] dark:bg-zinc-100 dark:text-zinc-900"
                >
                  <Plus className="h-4 w-4" />
                  Add Subscription
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
